import type { NriMatrixRow } from 'types/api-responses';

/**
 * Moves the highlight (the `inUse` row) to the GIS row of the in-use allocation
 * method — the row a GIS NRI / GIS WI value shows. With nothing in use,
 * nothing is highlighted.
 *
 * @param {NriMatrixRow[]} rows - Matrix rows as the API marks them.
 * @return {NriMatrixRow[]} The rows with only that GIS row marked.
 */
export const highlightGisCounterpart = (
  rows: NriMatrixRow[]
): NriMatrixRow[] => {
  const inUseMethod = rows.find((row) => row.inUse)?.allocationMethod;
  return rows.map((row) => ({
    ...row,
    inUse: row.source === 'gis' && row.allocationMethod === inUseMethod,
  }));
};

/**
 * The GIS row of the in-use allocation method — what a GIS NRI / GIS WI value
 * shows — or undefined when nothing is in use.
 *
 * @param {T[]} rows - Matrix rows with their allocation method and source.
 * @return {T | undefined} That GIS row.
 */
export const gisRowOf = <
  T extends Pick<NriMatrixRow, 'inUse' | 'allocationMethod' | 'source'>,
>(
  rows: T[]
): T | undefined => {
  const inUseMethod = rows.find((row) => row.inUse)?.allocationMethod;
  return rows.find(
    (row) => row.source === 'gis' && row.allocationMethod === inUseMethod
  );
};
